import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './ForgotPasswordModal.module.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const PASSWORD_COMPLEXITY_REGEX =
  /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}/;

const STEP = {
  ENTER_EMAIL: 'ENTER_EMAIL',
  ENTER_OTP: 'ENTER_OTP',
  RESET_PASSWORD: 'RESET_PASSWORD',
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

async function postPasswordReset(path, payload) {
  const url = `${API_BASE_URL}/api/auth/password-reset/${path}/`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let data;
  try {
    data = await response.json();
  } catch {
    data = {};
  }
  if (!response.ok) {
    const message = data.error || data.message || 'Something went wrong. Please try again.';
    throw new Error(message);
  }
  return data;
}

function ForgotPasswordModal({ initialEmail = '', onClose }) {
  const [step, setStep] = useState(STEP.ENTER_EMAIL);
  const [email, setEmail] = useState(initialEmail || '');
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const otpInputRefs = useRef([]);

  const isOtpExpired = useMemo(() => {
    if (!otpExpiry) return false;
    return Date.now() > otpExpiry;
  }, [otpExpiry]);

  useEffect(() => {
    const handler = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldownSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldownSeconds]);

  const safeClose = () => {
    if (loading) return;
    setNewPassword('');
    setConfirmPassword('');
    setOtpDigits(Array(OTP_LENGTH).fill(''));
    setResetToken('');
    onClose();
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      safeClose();
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Please go back and enter your email first.');
      return;
    }
    const trimmedEmail = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await postPasswordReset('resend', { email: trimmedEmail });
      const ttl = data.otp_ttl_seconds || 0;
      if (ttl > 0) setOtpExpiry(Date.now() + ttl * 1000);
      setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      setInfoMessage(
        data.message || 'If an account exists for this email, we have re-sent a one-time code.',
      );
    } catch (err) {
      console.error('[ForgotPassword] resend error', err);
      setError(err.message || 'Unable to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (step === STEP.ENTER_EMAIL) {
      const trimmedEmail = (email || '').trim().toLowerCase();
      if (!trimmedEmail) {
        setError('Please enter your email address.');
        return;
      }
      if (!EMAIL_REGEX.test(trimmedEmail)) {
        setError('Please enter a valid email address.');
        return;
      }
      setLoading(true);
      setError('');
      setInfoMessage('');
      try {
        const data = await postPasswordReset('request', { email: trimmedEmail });
        const ttl = data.otp_ttl_seconds || 0;
        if (ttl > 0) setOtpExpiry(Date.now() + ttl * 1000);
        setEmail(trimmedEmail);
        setStep(STEP.ENTER_OTP);
        setInfoMessage(
          data.message ||
            'If an account exists for this email, we have sent a one-time code to it.',
        );
        window.setTimeout(() => {
          if (otpInputRefs.current[0]) otpInputRefs.current[0].focus();
        }, 0);
      } catch (err) {
        console.error('[ForgotPassword] request error', err);
        setError(err.message || 'Unable to start password reset. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (step === STEP.ENTER_OTP) {
      const otp = otpDigits.join('');
      if (otp.length !== OTP_LENGTH) {
        setError(`Please enter the ${OTP_LENGTH}-digit code sent to your email.`);
        return;
      }
      const trimmedEmail = (email || '').trim().toLowerCase();
      if (!trimmedEmail) {
        setError('Email is missing. Please go back and enter your email again.');
        return;
      }
      setLoading(true);
      setError('');
      setInfoMessage('');
      try {
        const data = await postPasswordReset('verify-otp', { email: trimmedEmail, otp });
        if (!data.reset_token) throw new Error('Unexpected response from server.');
        setResetToken(data.reset_token);
        setStep(STEP.RESET_PASSWORD);
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        setInfoMessage('OTP verified. You can now set a new password.');
      } catch (err) {
        console.error('[ForgotPassword] verify error', err);
        setError(err.message || 'Invalid or expired OTP.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (step === STEP.RESET_PASSWORD) {
      if (!newPassword || !confirmPassword) {
        setError('Please fill in both password fields.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      if (!PASSWORD_COMPLEXITY_REGEX.test(newPassword)) {
        setError(
          'Password must be at least 8 characters long, contain an uppercase letter, a lowercase letter, a number, and a special character.',
        );
        return;
      }
      if (!resetToken) {
        setError('Your reset session has expired. Please restart the forgot password flow.');
        return;
      }
      setLoading(true);
      setError('');
      setInfoMessage('');
      try {
        const data = await postPasswordReset('confirm', {
          reset_token: resetToken,
          new_password: newPassword,
          confirm_password: confirmPassword,
        });
        setInfoMessage(
          data.message || 'Password reset successfully. You can now sign in with your new password.',
        );
        setNewPassword('');
        setConfirmPassword('');
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        window.setTimeout(() => safeClose(), 1600);
      } catch (err) {
        console.error('[ForgotPassword] confirm error', err);
        setError(
          err.message ||
            'Unable to reset password. Your link might have expired. Please restart the flow.',
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    setError('');
    setInfoMessage('');
    if (step === STEP.ENTER_OTP) {
      setStep(STEP.ENTER_EMAIL);
      setOtpDigits(Array(OTP_LENGTH).fill(''));
    } else if (step === STEP.RESET_PASSWORD) {
      setStep(STEP.ENTER_OTP);
    }
  };

  const getTitle = () => {
    if (step === STEP.ENTER_EMAIL) return 'Forgot password';
    if (step === STEP.ENTER_OTP) return 'Enter OTP';
    return 'Set a new password';
  };

  // --- INLINE UI (no CSS modules) ---
  const modalContent = (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{getTitle()}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={safeClose}
            disabled={loading}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {error && <div className={styles.errorMessage}>{error}</div>}
          {infoMessage && <div className={styles.infoMessage}>{infoMessage}</div>}

          {step === STEP.ENTER_EMAIL && (
            <>
              <p className={styles.description}>
                Enter the email linked to your Suspire account. If it exists, we&apos;ll send an OTP
                to help you reset your password.
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="forgot-email" className={styles.label}>
                  Email address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  className={styles.input}
                />
                <span className={styles.helperText}>
                  For security, we won&apos;t tell you whether this email is registered.
                </span>
              </div>
            </>
          )}

          {step === STEP.ENTER_OTP && (
            <>
              <p className={styles.description}>
                We&apos;ve sent a 6-digit OTP to your email if it&apos;s registered with Suspire. Enter it
                below to continue.
              </p>
              <div className={styles.otpInputs}>
                {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otpDigits[index]}
                    onChange={(e) => {
                      const digit = e.target.value.replace(/\D/g, '').slice(0, 1);
                      const next = [...otpDigits];
                      next[index] = digit;
                      setOtpDigits(next);

                      if (digit && index < OTP_LENGTH - 1) {
                        const nextRef = otpInputRefs.current[index + 1];
                        if (nextRef) {
                          nextRef.focus();
                        }
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
                        const prevRef = otpInputRefs.current[index - 1];
                        if (prevRef) prevRef.focus();
                      }
                    }}
                    ref={(el) => {
                      otpInputRefs.current[index] = el;
                    }}
                    className={styles.otpInput}
                  />
                ))}
              </div>
              <p className={styles.otpMeta}>
                {isOtpExpired
                  ? 'This code may have expired. You can request a new OTP.'
                  : 'OTP is valid for a short time. Do not share it with anyone.'}
              </p>
              <div className={styles.resendRow}>
                <button
                  type="button"
                  className={styles.resendButton}
                  onClick={handleResend}
                  disabled={loading || resendCooldownSeconds > 0}
                >
                  {resendCooldownSeconds > 0
                    ? `Resend in ${resendCooldownSeconds}s`
                    : 'Resend code'}
                </button>
              </div>
            </>
          )}

          {step === STEP.RESET_PASSWORD && (
            <>
              <p className={styles.description}>
                Choose a strong new password for your account. You&apos;ll use this to sign in going
                forward.
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="new-password" className={styles.label}>
                  New password
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    className={styles.passwordInput}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowNewPassword((prev) => !prev)}
                  >
                    {showNewPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="confirm-password" className={styles.label}>
                  Confirm new password
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    className={styles.passwordInput}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <span className={styles.passwordHint}>
                  Password must be at least 8 characters and include uppercase, lowercase, number, and a
                  special character.
                </span>
              </div>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={step === STEP.ENTER_EMAIL ? safeClose : handleBack}
            disabled={loading}
          >
            {step === STEP.ENTER_EMAIL ? 'Back to login' : 'Back'}
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handlePrimaryAction}
            disabled={loading}
          >
            {step === STEP.ENTER_EMAIL && (loading ? 'Sending...' : 'Get OTP')}
            {step === STEP.ENTER_OTP && (loading ? 'Verifying...' : 'Verify OTP')}
            {step === STEP.RESET_PASSWORD && (loading ? 'Saving...' : 'Reset password')}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default ForgotPasswordModal;


